import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, Filter, Pencil, Trash2, CheckCircle, XCircle } from "lucide-react";
import { ApplicationForm } from "@/components/forms/ApplicationForm";
import { ServerPagination } from "@/components/ui/server-pagination";
import { toast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { canManageModule } from "@/lib/permissions";

type Application = Tables<"applications">;

const Admission = () => {
  const { roles, user } = useAuth();
  const canManage = canManageModule(roles, "admission");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const queryClient = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["applications", debouncedSearch, currentPage, pageSize],
    queryFn: async () => {
      let q = supabase.from("applications").select("*", { count: "exact" }).order("created_at", { ascending: false });
      if (debouncedSearch) {
        const s = `%${debouncedSearch}%`;
        q = q.or(`applicant_name.ilike.${s},email.ilike.${s},program_applied.ilike.${s}`);
      }
      const from = (currentPage - 1) * pageSize;
      q = q.range(from, from + pageSize - 1);
      const { data, error, count } = await q;
      if (error) throw error;
      return { applications: data as Application[], totalCount: count ?? 0 };
    },
  });

  const applications = data?.applications ?? [];
  const totalCount = data?.totalCount ?? 0;

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("applications").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
      toast({ title: "Application deleted" });
    },
    onError: (error: any) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const approveMutation = useMutation({
    mutationFn: async (app: Application) => {
      const { error: updateError } = await supabase.from("applications").update({ status: "approved", reviewed_by: user?.id }).eq("id", app.id);
      if (updateError) throw updateError;

      const studentNumber = `STD-${Date.now().toString(36).toUpperCase()}`;
      const nameParts = app.applicant_name.split(" ");
      const firstName = nameParts[0] || app.applicant_name;
      const lastName = nameParts.slice(1).join(" ") || "";

      const { data: newStudent, error: studentError } = await supabase.from("students").insert({
        first_name: firstName,
        last_name: lastName || firstName,
        student_number: studentNumber,
        email: app.email,
        phone: app.phone,
        program: app.program_applied,
        status: "unregistered_inactive",
      }).select().single();
      if (studentError) throw studentError;

      const { data: feeStructure } = await supabase.from("fee_structures").select("*").eq("program", app.program_applied).limit(1).single();
      if (feeStructure && newStudent) {
        await supabase.from("invoices").insert({
          student_id: newStudent.id,
          amount: feeStructure.amount,
          description: `${feeStructure.program} - ${feeStructure.semester} ${feeStructure.academic_year} Tuition`,
          status: "unpaid",
          due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        });
      }

      await supabase.from("approvals").insert({
        type: "admission",
        record_id: app.id,
        requested_by: user!.id,
        status: "approved",
        reviewed_by: user!.id,
        reviewed_at: new Date().toISOString(),
      });

      await supabase.from("notifications").insert({
        user_id: user!.id,
        title: "Application Approved",
        message: `${app.applicant_name} has been admitted to ${app.program_applied}. Student record and invoice created.`,
        type: "approval",
        link: "/administration",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast({ title: "Application approved!", description: "Student record and initial invoice created automatically." });
    },
    onError: (error: any) => toast({ title: "Error approving", description: error.message, variant: "destructive" }),
  });

  const rejectMutation = useMutation({
    mutationFn: async (app: Application) => {
      const { error } = await supabase.from("applications").update({ status: "rejected", reviewed_by: user?.id }).eq("id", app.id);
      if (error) throw error;
      await supabase.from("approvals").insert({
        type: "admission",
        record_id: app.id,
        requested_by: user!.id,
        status: "rejected",
        reviewed_by: user!.id,
        reviewed_at: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
      toast({ title: "Application rejected" });
    },
    onError: (error: any) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div className="page-header">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Admission</h1>
            <p className="text-muted-foreground text-sm">Manage student applications and enrollment</p>
          </div>
          {canManage && <Button onClick={() => { setEditingApp(null); setFormOpen(true); }}><Plus className="mr-2 h-4 w-4" />New Application</Button>}
        </div>

        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search applications..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Button variant="outline"><Filter className="mr-2 h-4 w-4" />Filter</Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Applications</CardTitle>
            <CardDescription>{totalCount} applications found</CardDescription>
          </CardHeader>
          <CardContent className="table-responsive">
            {isLoading ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground">Loading...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Applicant Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No applications found</TableCell></TableRow>
                  ) : (
                    applications.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell className="font-medium">{app.applicant_name}</TableCell>
                        <TableCell>{app.email}</TableCell>
                        <TableCell>{app.program_applied}</TableCell>
                        <TableCell><StatusBadge status={app.status} /></TableCell>
                        <TableCell>{new Date(app.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {canManage && app.status === "pending" && (
                              <>
                                <Button variant="ghost" size="sm" title="Approve & Create Student" onClick={() => approveMutation.mutate(app)} disabled={approveMutation.isPending}>
                                  <CheckCircle className="h-4 w-4 text-primary" />
                                </Button>
                                <Button variant="ghost" size="sm" title="Reject" onClick={() => rejectMutation.mutate(app)} disabled={rejectMutation.isPending}>
                                  <XCircle className="h-4 w-4 text-destructive" />
                                </Button>
                              </>
                            )}
                            {canManage && <Button variant="ghost" size="sm" onClick={() => { setEditingApp(app); setFormOpen(true); }}><Pencil className="h-4 w-4" /></Button>}
                            {canManage && <Button variant="ghost" size="sm" onClick={() => setDeleteId(app.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
            <ServerPagination currentPage={currentPage} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} onPageSizeChange={setPageSize} />
          </CardContent>
        </Card>
      </div>

      <ApplicationForm open={formOpen} onOpenChange={setFormOpen} application={editingApp} onSuccess={() => queryClient.invalidateQueries({ queryKey: ["applications"] })} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
};

export default Admission;
